/** /block_lists/ — cross-tenant Block_List catalog (list + CRUD + used_by).
 *
 * A block list is an ordered, nestable grouping of notification blocks
 * (and/or other lists). Each item is EITHER a block OR a nested list, with
 * an optional `alias` overriding the name it appears under in the block
 * namespace. `tour_operator` is nullable: a null tenant is a system-wide list.
 *
 * Hand-authored wrapper (console convention: never depend on generated types
 * for feature modules). `items` is reconciled wholesale on write. No
 * optimistic-concurrency token — writes are last-writer-wins.
 */
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type { TenantRef } from './tours';
import type { PaginatedResponse } from './users';
import type { TenantIdsParam } from '@/scope/tenantIds';

/** Lightweight block reference embedded in a list item. */
export interface BlockRef {
  id: number;
  codename: string;
  name: string;
  type: string;
}

/** Lightweight (nested) list reference embedded in a list item. */
export interface BlockListRef {
  id: number;
  codename: string;
  name: string;
}

/** One ordered item: exactly one of `block` / `block_list` is set. */
export interface BlockListItem {
  id: number;
  order: number;
  /** Overrides the name the entry appears under in the block namespace. */
  alias: string | null;
  block: BlockRef | null;
  block_list: BlockListRef | null;
}

export interface BlockListListItem {
  id: number;
  /** Owning tenant. Null for system-wide lists. */
  tenant: TenantRef | null;
  codename: string;
  name: string;
  description: string;
  enabled: boolean;
  item_count: number;
  created_ts: string | null;
  updated_ts: string | null;
}

export interface BlockListDetail extends BlockListListItem {
  /** Ordered items. */
  items: BlockListItem[];
  /** Reference counts of the PROTECTed FKs that block a delete (409). */
  used_by: {
    notifications: number;
    lists: number;
  };
}

/** One item on write — exactly one of block_id / block_list_id. */
export interface BlockListWriteItem {
  block_id?: number;
  block_list_id?: number;
  alias?: string | null;
  order?: number;
}

/** Create/update body. `codename` required on create; `tour_operator` null =
 *  system-wide. `items`, when present, fully replaces the list's items
 *  (reconciled wholesale). Omit `items` to leave them untouched. */
export interface BlockListWrite {
  tour_operator?: number | null;
  codename?: string;
  name?: string;
  description?: string;
  enabled?: boolean;
  items?: BlockListWriteItem[];
}

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  /** codename + name + description (icontains). */
  search?: string;
  /** One id or a comma list of ids (D1), or 'system' / 'null' for system-wide lists (no tenant). */
  tour_operator_id?: TenantIdsParam;
  enabled?: boolean;
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  if (params.page) usp.set('page', String(params.page));
  if (params.pageSize) usp.set('page_size', String(params.pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.tour_operator_id !== undefined) {
    usp.set('tour_operator_id', String(params.tour_operator_id));
  }
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function list(params: ListParams = {}): Promise<PaginatedResponse<BlockListListItem>> {
  return apiGet<PaginatedResponse<BlockListListItem>>(`/block_lists/${toQuery(params)}`);
}

export function retrieve(id: number): Promise<BlockListDetail> {
  return apiGet<BlockListDetail>(`/block_lists/${id}/`);
}

export function create(body: BlockListWrite): Promise<BlockListDetail> {
  return apiPost<BlockListDetail>('/block_lists/', body);
}

export function update(id: number, body: BlockListWrite): Promise<BlockListDetail> {
  return apiPatch<BlockListDetail>(`/block_lists/${id}/`, body);
}

export function remove(id: number): Promise<void> {
  return apiDelete(`/block_lists/${id}/`);
}
